import {
  Badge,
  Box,
  Button,
  Center,
  createStyles,
  Drawer,
  Skeleton,
  Text,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import NoteModalNew from "../../../components/NoteModal/NoteModalNew";
import { useSelector } from "react-redux";
import moment from "moment";
import { getSingleNote } from "../../../services/note";
import DocumentNotificationCard from "./DocumentActions";
import { decrypt } from "../../../utils";
import { getPendingActions } from "../../../services/dashboard";
import CustomBillingPagination from "../../../components/Pagination/CustomBillingPagination";
import { PendingActionsHeader } from "./PendingAction";

const useStyles = createStyles(() => ({
  drawer: {
    ".mantine-Drawer-content": {
      backgroundColor: "#f5f5f5",
    },
    ".mantine-Drawer-title": {
      fontSize: 20,
      fontWeight: 700,
      fontFamily: "Inter",
      color: "#000",
    },
    ".mantine-Drawer-header": {
      backgroundColor: "#fff",
    },
  },
  container: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "5px",
    display: "flex",
    flexDirection: "column",
    // height: "100vh",
    height: "1000px",
    overflowY: "auto",
    gap: "1rem",
    // Adding scrollbar styles
    scrollbarWidth: "thin",
    scrollbarColor: "#E5E5E5",
    "::-webkit-scrollbar": {
      width: "10px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f0f0f0",
      borderRadius: "10px",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "10px",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: "#E5E5E5",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  card: {
    borderRadius: "8px",
    padding: ".5rem",
    backgroundColor: "#f5f5f5",
  },
  title: {
    borderRadius: "4px",
    padding: "0.3rem",
    borderLeftWidth: "5px",
    borderLeftStyle: "solid",
  },
  noteCardTitle: {
    backgroundColor: "#FEF0C7",
    borderLeftColor: "#CC7914",
  },
  taskCardTitle: {
    backgroundColor: "#F6F0FF",
    borderLeftColor: "#D6BEF5",
  },
  medicationCardTitle: {
    backgroundColor: "#DCFFCC",
    borderLeftColor: "#1EA736",
  },
  documentCardTitle: {
    backgroundColor: "#E0F4FF",
    borderLeftColor: "#1791D3",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: ".1rem",
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "0.5rem",
    borderColor: "#E5E5EA",
    borderWidth: "2px",
    borderStyle: "solid",
    marginTop: "0.5rem",
  },
  taskCard: {
    borderRadius: "10px",
    padding: ".5rem",
    backgroundColor: "#f5f5f5",
  },
  taskCardContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
  },
}));

const PendingActionsDrawer = ({
  opened,
  close,
  setSelectedNote,
  setIsOpen,
}) => {
  const { profile } = useSelector((state) => {
    return state?.profile;
  });
  const { staff } = useSelector((state) => state.staff);
  const {
    dashboardPendingActions,
    dashboardPendingActionsLoading,
    dashboardPendingActionsCount,
  } = useSelector((state) => state?.dashboard);
  const hasPermissions = profile?.user?.permissions;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    getPendingActions(currentPage);
  }, [currentPage]);

  const { classes } = useStyles();

  return (
    <Drawer
      size="35%"
      offset={8}
      radius="md"
      zIndex={1000}
      position="right"
      title={
        <Box>
          <PendingActionsHeader
            classes={classes}
            loading={dashboardPendingActionsLoading}
            count={dashboardPendingActionsCount}
            actions={dashboardPendingActions}
          />
        </Box>
      }
      opened={opened}
      className={classes.drawer}
      onClose={close}
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
    >
      {dashboardPendingActionsLoading ? (
        Array.from({ length: 10 }).map((_, index) => (
          <Skeleton
            key={index}
            mb="md"
            radius="md"
            my={15}
            height={200}
            color="gray"
            c={"#E5E5E5"}
            visible={true}
          />
        ))
      ) : dashboardPendingActions?.length > 0 ? (
        <>
          <Box py={15}>
            {dashboardPendingActions
              ?.filter((item) => {
                if (item.type === "document") return true;
                if (!hasPermissions) return true; // show notes if permission object doesn't exist
                return profile?.user?.permissions?.note === true;
              })
              ?.map((item, index) => {
                if (item?.type === "document") {
                  return (
                    <DocumentNotificationCard key={item?._id} item={item} />
                  );
                } else if (item.type === "note") {
                  return (
                    <Box className={classes.card} key={index}>
                      <Box
                        className={`${classes.title} ${classes.noteCardTitle}`}
                      >
                        <Text ff={"Inter"} fz={12} fw={700}>
                          Note
                        </Text>
                      </Box>
                      <Box className={classes.body}>
                        <Badge
                          size="md"
                          radius="xl"
                          color="gray"
                          c={"#525252"}
                          tt={"uppercase"}
                          w={"fit-content"}
                        >
                          {item?.fields?.noteType || item?.noteId?.name}
                        </Badge>
                        <Text ff={"Inter"} fz={24} fw={700}>
                          {
                            staff?.staffs?.find(
                              (staffMember) =>
                                item?.createdBy === staffMember?.id
                            )?.name
                          }
                        </Text>
                        <Text ff={"Inter"} fz={14} c={"#A3A3A3"} fw={500}>
                          {moment(item?.noteDate || item?.createdAt).format(
                            "MMM DD, hh:mmA"
                          )}
                        </Text>
                        {/* <Text ff={"Inter"} fz={14} c={"#737373"} fw={400}>
                      {item?.fields?.Description || "No Description Found"}
                    </Text> */}

                        <Text ff={"Inter"} fz={14} c={"#737373"} fw={400}>
                          {item?.fields?.Description
                            ? item.fields.Description.length > 100
                              ? `${item.fields.Description.slice(0, 100)}...`
                              : item.fields.Description
                            : "No Description Found"}
                        </Text>

                        <Center>
                          <Button
                            fz={12}
                            fw={500}
                            fullWidth
                            c={"#000"}
                            ff={"Inter"}
                            variant="white"
                            onClick={() => {
                              setSelectedNote(item);
                              setIsOpen(true);
                            }}
                          >
                            View Details
                          </Button>
                        </Center>
                      </Box>
                    </Box>
                  );
                }
              })}
            <CustomBillingPagination
              data={dashboardPendingActionsCount}
              itemsPerPage={20}
              currentPage={currentPage}
              handlePageChange={(newPage) => {
                setCurrentPage(newPage);
              }}
            />
          </Box>
        </>
      ) : (
        <Text
          ff={"Inter"}
          fz={16}
          c={"#737373"}
          fw={500}
          align="center"
          my={"auto"}
        >
          No pending actions found.
        </Text>
      )}
    </Drawer>
  );
};

export default PendingActionsDrawer;
